import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, bindActionCreators, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app, { createReducer } from './reducers';
import { historyMiddleware } from './Middlewares/History.middleware';
import { add as addToCart, load } from './Actions/Cart.action';
import MiniCart from './Components/MiniCart';
import Navigation from './Components/Navigation';
import FacetedSearch from './Components/FacetedSearch';
import FacetedSearchCompactContainer from './Components/FacetedSearchCompactContainer';
import DynamicComponent from './Components/DynamicComponent';
import CookieBannerContainer from './Containers/CookieBanner.container';
import {
  setImages as setProductImageSlideShows,
  show as showProductImageSlideShow,
  setCurrentIndex as setProductImageSlideShowIndex,
} from './Actions/LightboxImages.action';
import { translate } from './Services/translation';
import './Components/Vanilla';
import 'react-tooltip/dist/react-tooltip.css';

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
const store = createStore(
  app,
  preloadState,
  composeWithDevTools(applyMiddleware(thunk, historyMiddleware))
);

// Add a dictionary to keep track of the registered async reducers
store.asyncReducers = {};

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
store.injectReducer = (key, asyncReducer) => {
  if (!store.asyncReducers[key]) {
    store.asyncReducers[key] = asyncReducer;
    store.replaceReducer(createReducer(store.asyncReducers));
  }
};

window.__litium = {
  ...window.__litium,
  reduxActions: bindActionCreators(
    {
      addToCart,
      setProductImageSlideShows,
      showProductImageSlideShow,
      setProductImageSlideShowIndex,
      load,
    },
    store.dispatch
  ),
  events: {
    onAddToCartButtonClick: (
      sourceDomNode,
      articleNumber,
      quantityFieldId = null
    ) => {
      let input = document.getElementById(quantityFieldId);

      // if no quantity is selected...
      if (document.getElementById(quantityFieldId).value == '') {
        let sibling = input.nextElementSibling;
        sibling.innerHTML = 'Required';
        setTimeout(function () {
          sibling.innerHTML = '';
        }, 3000);
      }

      const nodeIdToShowNotification = (sourceDomNode.id = Date.now());
      window.__litium.reduxActions.addToCart({
        articleNumber,
        quantity: quantityFieldId
          ? document.getElementById(quantityFieldId).value
          : 1,
        nodeIdToShowNotification,
        notificationMessage: translate('tooltip.addedtocart'),
        hash: Date.now(),
      });

      document.getElementById(quantityFieldId).value = '';
    },
    onReorderClick: (orderid) => {
      if (orderid) {
        window.__litium.reduxActions.reorder({ orderid });
      }
    },
    onProductImageClick: (ele) => {
      const productImagesSelector = 'product-images';
      const productImageSelector = 'product-image';
      const closest = (el, className) =>
        el
          ? el.classList.contains(className)
            ? el
            : closest(el.parentNode, className)
          : null;
      const parentNodes = ele.closest
        ? ele.closest(`.${productImagesSelector}`)
        : closest(ele, productImagesSelector);
      const images = Array.from(
        parentNodes.querySelectorAll(`.${productImageSelector}`)
      ).map((img) => ({ src: img.dataset.src }));
      const index = images.findIndex((_ele) => ele.dataset.src === _ele.src);

      window.__litium.reduxActions.setProductImageSlideShows(images);
      window.__litium.reduxActions.showProductImageSlideShow(true);
      window.__litium.reduxActions.setProductImageSlideShowIndex(index);
    },
  },
  bootstrapComponents: () => {
    // bootstrap React components, in case the HTML response we receive from the server
    // has React components. ReactDOM.render performs only an update on previous rendered
    // components and only mutate the DOM as necessary to reflect latest React element.
    bootstrapComponents();
  },
  cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
  registeredContainers.push(container);
  ReactDOM.render(element, container, callback);
};

window.onunload = () => {
  // make sure components are unmounted, redux's listener are unsubscribed
  // to fix issue with iframe navigation in IE
  registeredContainers.forEach((container) => {
    ReactDOM.unmountComponentAtNode(container);
  });
};

const bootstrapComponents = () => {
  if (document.getElementById('miniCart')) {
    renderReact(
      <Provider store={store}>
        <MiniCart />
      </Provider>,
      document.getElementById('miniCart')
    );
  }

  const clerkQuickSearchContainers = document.querySelectorAll(
    '.header__clerk-quick-search'
  );
  if (!!clerkQuickSearchContainers.length) {
    const ClerkQuickSearch = DynamicComponent({
      loader: () => import('./Components/Clerk/ClerkQuickSearch'),
    });
    Array.from(clerkQuickSearchContainers).forEach((input) => {
      renderReact(
        <Provider store={store}>
          <ClerkQuickSearch />
        </Provider>,
        input
      );
    });
  }

  const clerkQuickSearchResultContainers = document.querySelectorAll(
    '.header__clerk-quick-search-result'
  );
  if (!!clerkQuickSearchResultContainers.length) {
    const ClerkQuickSearchResult = DynamicComponent({
      loader: () => import('./Components/Clerk/ClerkQuickSearchResult'),
    });
    Array.from(clerkQuickSearchResultContainers).forEach((elm) => {
      renderReact(
        <Provider store={store}>
          <ClerkQuickSearchResult />
        </Provider>,
        elm
      );
    });
  }

  const quickSearchContainers = document.querySelectorAll(
    '.header__quick-search'
  );
  if (!!quickSearchContainers.length) {
    const QuickSearch = DynamicComponent({
      loader: () => import('./Components/QuickSearch/QuickSearch'),
    });
    Array.from(quickSearchContainers).forEach((input) => {
      renderReact(
        <Provider store={store}>
          <QuickSearch />
        </Provider>,
        input
      );
    });
  }

  const quickSearchResultContainers = document.querySelectorAll(
    '.header__quick-search-result'
  );
  if (!!quickSearchResultContainers.length) {
    const QuickSearchResult = DynamicComponent({
      loader: () => import('./Components/QuickSearch/QuickSearchResult'),
    });
    Array.from(quickSearchResultContainers).forEach((elem) => {
      renderReact(
        <Provider store={store}>
          <QuickSearchResult />
        </Provider>,
        elem
      );
    });
  }

  if (document.getElementById('cookiebannerform')) {
    ReactDOM.render(
      <Provider store={store}>
        <CookieBannerContainer />
      </Provider>,
      document.getElementById('cookiebannerform')
    );
  }

  if (document.getElementById('quickSearchStart')) {
    ReactDOM.render(
      <Provider store={store}>
        <QuickSearchContainer />
      </Provider>,
      document.getElementById('quickSearchStart')
    );
  }
  if (document.getElementById('navbar')) {
    renderReact(
      <Provider store={store}>
        <Navigation />
      </Provider>,
      document.getElementById('navbar')
    );
  }
  const navigationWithMegaMenu = document.getElementById('navigation');
  if (navigationWithMegaMenu) {
    const NavigationWithMegaMenu = DynamicComponent({
      loader: () => import('./Components/Navigation/Navigation'),
    });
    renderReact(
      <Provider store={store}>
        <NavigationWithMegaMenu />
      </Provider>,
      navigationWithMegaMenu
    );
  }
  if (document.getElementById('facetedSearch')) {
    renderReact(
      <Provider store={store}>
        <FacetedSearch />
      </Provider>,
      document.getElementById('facetedSearch')
    );
  }
  if (document.getElementById('facetedSearchCompact')) {
    renderReact(
      <Provider store={store}>
        <FacetedSearchCompactContainer />
      </Provider>,
      document.getElementById('facetedSearchCompact')
    );
  }
  if (document.getElementById('myPagePersons')) {
    const PersonList = DynamicComponent({
      loader: () => import('./Components/PersonListContainer'),
    });
    renderReact(
      <Provider store={store}>
        <PersonList />
      </Provider>,
      document.getElementById('myPagePersons')
    );
  }
  if (document.getElementById('myPageAddresses')) {
    const AddressList = DynamicComponent({
      loader: () => import('./Components/AddressListContainer'),
    });
    renderReact(
      <Provider store={store}>
        <AddressList />
      </Provider>,
      document.getElementById('myPageAddresses')
    );
  }
  if (document.getElementById('checkout')) {
    const Checkout = DynamicComponent({
      loader: () => import('./Components/Checkout'),
    });
    renderReact(
      <Provider store={store}>
        <Checkout />
      </Provider>,
      document.getElementById('checkout')
    );
  }
  const alternativeCheckout = document.getElementById('alternative-checkout');
  if (alternativeCheckout) {
    const AlternativeCheckout = DynamicComponent({
      loader: () => import('./Containers/AlternativeCheckout.container'),
    });
    renderReact(
      <Provider store={store}>
        <AlternativeCheckout />
      </Provider>,
      alternativeCheckout
    );
  }
  const alternativeCheckoutSupportTrigger = document.getElementById(
    'alternative-checkout-support-trigger'
  );
  if (alternativeCheckoutSupportTrigger) {
    const AlternativeCheckoutSupportTrigger = DynamicComponent({
      loader: () =>
        import(
          './Components/AlternativeCheckout/AlternativeCheckout.SupportTrigger'
        ),
    });
    renderReact(
      <Provider store={store}>
        <AlternativeCheckoutSupportTrigger />
      </Provider>,
      alternativeCheckoutSupportTrigger
    );
  }
  if (document.getElementById('lightBoxImages')) {
    const LightboxImages = DynamicComponent({
      loader: () => import('./Components/LightboxImages'),
    });
    import('./Reducers/LightboxImages.reducer').then(({ lightboxImages }) => {
      store.injectReducer('lightboxImages', lightboxImages);
      renderReact(
        <Provider store={store}>
          <LightboxImages />
        </Provider>,
        document.getElementById('lightBoxImages')
      );
    });
  }

  if (document.querySelectorAll('.slider').length > 0) {
    const Slider = DynamicComponent({
      loader: () => import('./Components/Slider'),
    });
    Array.from(document.querySelectorAll('.slider')).forEach(
      (slider, index) => {
        const values = [...slider.querySelectorAll('template')].map((block) => {
          return {
            html: block.innerHTML,
          };
        });
        if (values.length > 0) {
          renderReact(<Slider values={values} />, slider);
        }
      }
    );
  }

  if (document.getElementById('orderHistoryPage')) {
    const OrderList = DynamicComponent({
      loader: () => import('./Components/OrderHistoryListContainer'),
    });
    renderReact(
      <Provider store={store}>
        <OrderList />
      </Provider>,
      document.getElementById('orderHistoryPage')
    );
  }

  const headerButtonContainer = document.getElementById(
    'header-button-container'
  );
  if (headerButtonContainer) {
    const HeaderButtonContainer = DynamicComponent({
      loader: () => import('./Containers/HeaderButton.container'),
    });

    const jsonDataElement = headerButtonContainer.querySelector('json-data');

    const headerButtonProps =
      jsonDataElement && jsonDataElement.textContent
        ? JSON.parse(jsonDataElement.textContent)
        : {};

    renderReact(
      <Provider store={store}>
        <HeaderButtonContainer {...headerButtonProps} />
      </Provider>,
      headerButtonContainer
    );
  }

  const razorIcons = document.querySelectorAll('.razor-icon');
  if (!!razorIcons.length) {
    const Icon = DynamicComponent({
      loader: () => import('./Components/Icon'),
    });
    Array.from(razorIcons).forEach((razorIcon) => {
      const { name, size, className } = razorIcon.dataset;
      renderReact(
        <Icon name={name} size={size} className={className} />,
        razorIcon
      );
    });
  }

  if (document.getElementById('test-container')) {
    const TestContainer = DynamicComponent({
      loader: () => import('./Components/TestContainer'),
    });
    renderReact(
      <Provider store={store}>
        <TestContainer />
      </Provider>,
      document.getElementById('test-container')
    );
  }

  if (document.getElementById('overlayContainer')) {
    const Overlay = DynamicComponent({
      loader: () => import('./Components/Overlay'),
    });
    renderReact(
      <Provider store={store}>
        <Overlay />
      </Provider>,
      document.getElementById('overlayContainer')
    );
  }

  const wishListButton = document.querySelectorAll('wishlist-button');
  if (wishListButton.length > 0) {
    const FavoritesButton = DynamicComponent({
      loader: () => import('./Components/WishListButton.container'),
    });
    Array.from(wishListButton).forEach((button) => {
      const {
        articleNumber,
        quantityFieldId,
        href,
        cssClass,
        label,
      } = button.dataset;
      renderReact(
        <Provider store={store}>
          <FavoritesButton
            {...{
              label,
              articleNumber,
              quantityFieldId,
              href,
              cssClass,
            }}
          />
        </Provider>,
        button
      );
    });
  }

  const wishList = document.getElementById('wishList');
  if (wishList) {
    const WishList = DynamicComponent({
      loader: () => import('./Components/WishList.container'),
    });
    renderReact(
      <Provider store={store}>
        <WishList />
      </Provider>,
      wishList
    );
  }

  if (document.getElementById('toastContainer')) {
    const ToastContainer = DynamicComponent({
      loader: () => import('./Components/Toast/ToastContainer'),
    });
    renderReact(
      <Provider store={store}>
        <ToastContainer />
      </Provider>,
      document.getElementById('toastContainer')
    );
  }

  const loginModal = document.getElementById('loginModal');
  if (loginModal) {
    const LoginModal = DynamicComponent({
      loader: () => import('./Components/Login/LoginModal'),
    });
    renderReact(
      <Provider store={store}>
        <LoginModal />
      </Provider>,
      loginModal
    );
  }

  const registerAccountForm = document.getElementById('register-account-form');
  if (registerAccountForm) {
    const RegisterAccountForm = DynamicComponent({
      loader: () => import('./Components/RegisterAccountForm'),
    });
    const registerAccountFormProps = JSON.parse(
      registerAccountForm.querySelector('json-data').textContent
    );
    renderReact(
      <Provider store={store}>
        <RegisterAccountForm {...registerAccountFormProps} />
      </Provider>,
      document.getElementById('register-account-form')
    );
  }

  const productPage = document.getElementById('product-page');
  if (productPage) {
    const ProductPage = DynamicComponent({
      loader: () => import('./Containers/ProductPage.container'),
    });
    const productPageProps = JSON.parse(
      productPage.querySelector('json-data').textContent
    );
    renderReact(
      <Provider store={store}>
        <ProductPage {...productPageProps} />
      </Provider>,
      productPage
    );
  }

  const categoryListing = document.getElementById('category-listing');
  if (categoryListing) {
    const CategoryListing = DynamicComponent({
      loader: () => import('./Containers/CategoryListing.container'),
    });
    const categoryListingProps = JSON.parse(
      categoryListing.querySelector('json-data').textContent
    );
    renderReact(
      <Provider store={store}>
        <CategoryListing {...categoryListingProps} />
      </Provider>,
      categoryListing
    );
  }

  const searchListing = document.getElementById('search-results-component');
  if (searchListing) {
    const SearchListing = DynamicComponent({
      loader: () => import('./Containers/SearchListing.container'),
    });
    const searchListingProps = JSON.parse(
      searchListing.querySelector('json-data').textContent
    );
    renderReact(
      <Provider store={store}>
        <SearchListing {...searchListingProps} />
      </Provider>,
      searchListing
    );
  }

  const loginContainer = document.getElementById('login-container');
  if (loginContainer) {
    const Login = DynamicComponent({
      loader: () => import('./Containers/Login.container'),
    });
    renderReact(
      <Provider store={store}>
        <Login />
      </Provider>,
      loginContainer
    );
  }

  const loginSelectOrganization = document.getElementById(
    'login-select-organization'
  );
  if (loginSelectOrganization) {
    const loginSelectOrganizationDataElement = loginSelectOrganization.querySelector(
      'json-data'
    );
    const loginSelectOrganizationData =
      loginSelectOrganizationDataElement &&
      loginSelectOrganizationDataElement.innerHTML
        ? JSON.parse(loginSelectOrganizationDataElement.innerHTML)
        : [];
    const LoginSelectOrganizationPage = DynamicComponent({
      loader: () => import('./Components/Login/ui/LoginSelectOrganizationPage'),
    });
    renderReact(
      <Provider store={store}>
        <LoginSelectOrganizationPage {...loginSelectOrganizationData} />
      </Provider>,
      loginSelectOrganization
    );
  }

  const myPagesContainer = document.getElementById('my-pages');
  if (myPagesContainer) {
    const myPagesDataElement = myPagesContainer.querySelector('json-data');
    const myPagesData =
      myPagesDataElement && myPagesDataElement.innerHTML
        ? JSON.parse(myPagesDataElement.innerHTML)
        : [];
    const MyPages = DynamicComponent({
      loader: () => import('./Containers/MyPages.container'),
    });
    renderReact(
      <Provider store={store}>
        <MyPages {...myPagesData} />
      </Provider>,
      myPagesContainer
    );
  }

  const miniCartModal = document.getElementById('miniCartModal');
  if (miniCartModal) {
    const MiniCart = DynamicComponent({
      loader: () => import('./Components/MiniCart/MiniCart'),
    });
    renderReact(
      <Provider store={store}>
        <MiniCart />
      </Provider>,
      miniCartModal
    );
  }

  const navigationMobile = document.getElementById('navigationMobile');
  if (navigationMobile) {
    const NavigationMobile = DynamicComponent({
      loader: () => import('./Components/Navigation/NavigationMobile'),
    });
    renderReact(
      <Provider store={store}>
        <NavigationMobile />
      </Provider>,
      navigationMobile
    );
  }

  const productsBlocks = document.querySelectorAll('.products-block');
  if (!!productsBlocks.length) {
    const ProductsBlock = DynamicComponent({
      loader: () => import('./Blocks/Products/Products'),
    });
    Array.from(productsBlocks).forEach((block) => {
      const productsBlockProps = JSON.parse(
        block.querySelector('json-data').textContent
      );
      renderReact(
        <Provider store={store}>
          <ProductsBlock {...productsBlockProps} />
        </Provider>,
        block
      );
    });
  }

  const quickOrderPage = document.getElementById('quick-order-container');
  if (quickOrderPage) {
    const QuickOrderPage = DynamicComponent({
      loader: () => import('./Containers/QuickOrder.container'),
    });
    const quickOrderPageProps = JSON.parse(
      quickOrderPage.querySelector('json-data').textContent
    );
    renderReact(
      <Provider store={store}>
        <QuickOrderPage {...quickOrderPageProps} />
      </Provider>,
      quickOrderPage
    );
  }

  const orderDetail = document.getElementById('order-detail');
  if (orderDetail) {
    const OrderDetail = DynamicComponent({
      loader: () => import('./Components/OrderDetail/OrderDetail'),
    });
    const orderDetailProps = JSON.parse(
      orderDetail.querySelector('json-data').textContent
    );
    renderReact(
      <Provider store={store}>
        <OrderDetail {...orderDetailProps} />
      </Provider>,
      orderDetail
    );
  }

  const newsListPagination = document.getElementById('news-list-pagination');
  if (newsListPagination) {
    const NewsListPagination = DynamicComponent({
      loader: () => import('./Components/ListingPagination'),
    });
    const newsListPaginationProps = JSON.parse(
      newsListPagination.querySelector('json-data').textContent
    );
    renderReact(
      <Provider store={store}>
        <NewsListPagination {...newsListPaginationProps} />
      </Provider>,
      newsListPagination
    );
  }
};

bootstrapComponents();

var sliderStock = document.querySelector(
  '.toggle-filters .toggle-filter .switch .slider.stock'
);
var sliderPriced = document.querySelector(
  '.toggle-filters .toggle-filter .switch .slider.priced'
);
var productView = document.querySelector('.SCProductView');

if (sliderStock && sliderStock.length !== 0) {
  // Changes slider to toggle nostock items
  sliderStock.addEventListener('click', function (event) {
    if (productView.getAttribute('hide-on-nostock') == 'false') {
      productView.setAttribute('hide-on-nostock', true);
    } else {
      productView.setAttribute('hide-on-nostock', false);
    }
  });
}

if (sliderPriced && sliderPriced.length !== 0) {
  // Changes slider to toggle noprice items
  sliderPriced.addEventListener('click', function (event) {
    if (productView.getAttribute('hide-on-noprice') == 'false') {
      productView.setAttribute('hide-on-noprice', true);
    } else {
      productView.setAttribute('hide-on-noprice', false);
    }
  });
}
